import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { formatDay } from '../../format.js';
import { TOKENS } from '../../tokens.js';
import type { PurchaseOrderDetail } from './types.js';

/**
 * Bir dilin metin fonksiyonu (i18next `getFixedT` ya da worker'ın İngilizce sözlüğü). Burada
 * tanımlıdır: bu modül arayüz bileşenlerine bağlanmaz (worker da kullanır).
 */
export type PdfT = (key: string, options?: Record<string, unknown>) => string;

/**
 * Siparişin PDF'i (ADR 0040 #181; `satin-alma-modulu.md` §7): tedarikçiye giden belge. Saf
 * fonksiyon: PO kartının verisinden pdfmake belge tanımı. Ofiste yazdırma ve indirme, gemide salt
 * okunur kopya ve worker'ın e-posta eki aynı tanımı kullanır (`@felix/ui/purchase-order-pdf`).
 * Onaylanmamış ya da iptal edilmiş PO'da uyarı bandı vardır: tedarikçiye gönderilmemelidir.
 */

export type PurchaseOrderPdfOrder = Pick<
  PurchaseOrderDetail,
  | 'poNo'
  | 'status'
  | 'orderDate'
  | 'vesselName'
  | 'requisition'
  | 'supplier'
  | 'currency'
  | 'expectedDelivery'
  | 'deliveryLocation'
  | 'deliveryTerms'
  | 'paymentTerms'
  | 'warranty'
  | 'remarks'
  | 'supplierRef'
  | 'charges'
  | 'itemsTotal'
  | 'total'
> & {
  lines: Pick<
    PurchaseOrderDetail['lines'][number],
    'lineNo' | 'name' | 'description' | 'quantity' | 'unit' | 'unitPrice' | 'total'
  >[];
};

export interface PurchaseOrderPdfInput {
  order: PurchaseOrderPdfOrder;
  /** Şirket markası (ADR 0019 §4): yoksa boş. */
  company: { name: string | null };
  /** Üretim zamanı, ISO-8601 UTC; altbilgide. */
  generatedAt: string;
}

const prefix = 'purchasing.po.pdf';
const FONT = 'Roboto';

/** Tutar: iki ondalık, binlik ayırıcı; belge dilden bağımsız (tedarikçiye; `en-US`). */
export function pdfMoney(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Dosya adı: ASCII, boşluksuz (ör. `purchase-order-PO-2026-078.pdf`). */
export function purchaseOrderPdfFileName(poNo: string, t: PdfT): string {
  return `${t(`${prefix}.fileName`)}-${poNo.replace(/[^A-Za-z0-9-]+/g, '-')}.pdf`;
}

const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};

/** pdfmake belge tanımı: A4 dikey, tek dil (İngilizce varsayılan; `t` verilen dilde). */
export function purchaseOrderPdf(input: PurchaseOrderPdfInput, t: PdfT): TDocumentDefinitions {
  const { order } = input;
  const label = (key: string) => t(`${prefix}.${key}`);
  const fact = (key: string, value: string | null): TableCell[] => [
    { text: label(key), style: 'label' },
    { text: value ?? '—' },
  ];
  const banner =
    order.status === 'draft' || order.status === 'pending_approval'
      ? label('notApproved')
      : order.status === 'cancelled'
        ? label('cancelled')
        : null;
  const charges: [string, number][] = (
    [
      ['discount', -order.charges.discount],
      ['freight', order.charges.freight],
      ['customs', order.charges.customs],
      ['tax', order.charges.tax],
      ['serviceFee', order.charges.serviceFee],
    ] as [string, number][]
  ).filter(([, value]) => value !== 0);
  const money = (value: number) => pdfMoney(value);
  const totals: TableCell[][] = [
    ...(charges.length > 0
      ? [
          [
            { text: label('itemsTotal'), style: 'label' },
            { text: money(order.itemsTotal), alignment: 'right' as const },
          ],
          ...charges.map(([key, value]): TableCell[] => [
            { text: label(key), style: 'label' },
            { text: money(value), alignment: 'right' },
          ]),
        ]
      : []),
    [
      { text: `${label('total')} (${order.currency})`, bold: true },
      { text: money(order.total), bold: true, alignment: 'right' },
    ],
  ];
  const content: Content[] = [
    {
      columns: [
        {
          width: '*',
          stack: [
            { text: input.company.name ?? '', style: 'company' },
            { text: order.vesselName, style: 'muted' },
          ],
        },
        {
          width: 'auto',
          stack: [
            { text: label('title'), style: 'title', alignment: 'right' },
            { text: order.poNo, style: 'poNo', alignment: 'right' },
            {
              text: `${label('date')}: ${formatDay(order.orderDate)}`,
              alignment: 'right',
              style: 'muted',
            },
          ],
        },
      ],
    },
  ];
  if (banner) {
    content.push({
      text: banner,
      style: 'banner',
      color: order.status === 'cancelled' ? TOKENS['danger-fg'] : TOKENS['warning-fg'],
      margin: [0, 10, 0, 0],
    });
  }
  content.push(
    {
      margin: [0, 14, 0, 0],
      columns: [
        {
          width: '*',
          stack: [
            { text: label('supplier'), style: 'heading' },
            { text: order.supplier.name, bold: true },
            { text: order.supplier.code, style: 'muted' },
            ...(order.supplier.contactPerson ? [{ text: order.supplier.contactPerson }] : []),
            ...(order.supplier.email ? [{ text: order.supplier.email }] : []),
            ...(order.supplier.phone ? [{ text: order.supplier.phone }] : []),
          ],
        },
        {
          width: '*',
          table: {
            widths: ['auto', '*'],
            body: [
              fact('vessel', order.vesselName),
              fact('requisition', order.requisition.requisitionNo),
              fact('currency', order.currency),
              fact('deliveryLocation', order.deliveryLocation),
              fact(
                'expectedDelivery',
                order.expectedDelivery ? formatDay(order.expectedDelivery) : null,
              ),
              fact('deliveryTerms', order.deliveryTerms),
              fact('paymentTerms', order.paymentTerms),
              fact('warranty', order.warranty),
              ...(order.supplierRef ? [fact('supplierRef', order.supplierRef)] : []),
            ],
          },
          layout: 'noBorders',
        },
      ],
    },
    {
      margin: [0, 16, 0, 0],
      table: {
        headerRows: 1,
        widths: [16, '*', 'auto', 36, 32, 64, 64],
        body: [
          [
            { text: '#', style: 'th' },
            { text: label('material'), style: 'th' },
            { text: label('description'), style: 'th' },
            { text: label('qty'), style: 'th', alignment: 'right' },
            { text: label('uom'), style: 'th' },
            { text: label('unitPrice'), style: 'th', alignment: 'right' },
            { text: label('lineTotal'), style: 'th', alignment: 'right' },
          ],
          ...order.lines.map((line): TableCell[] => [
            { text: String(line.lineNo) },
            { text: line.name },
            { text: line.description ?? '' },
            { text: String(line.quantity), alignment: 'right' },
            { text: line.unit },
            { text: money(line.unitPrice), alignment: 'right' },
            { text: money(line.total), alignment: 'right' },
          ]),
        ],
      },
      layout: LINES,
    },
    {
      margin: [0, 8, 0, 0],
      columns: [
        { width: '*', text: '' },
        {
          width: 'auto',
          table: { widths: ['auto', 80], body: totals },
          layout: 'noBorders',
        },
      ],
    },
  );
  if (order.remarks) {
    content.push({ text: label('remarks'), style: 'heading', margin: [0, 14, 0, 0] });
    content.push({ text: order.remarks });
  }
  return {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 48],
    info: { title: `${label('title')} ${order.poNo}`, creator: 'Felixmate' },
    defaultStyle: { font: FONT, fontSize: 9, color: TOKENS['navy-900'] },
    styles: {
      company: { fontSize: 13, bold: true },
      title: { fontSize: 16, bold: true },
      poNo: { fontSize: 12, bold: true, color: TOKENS['primary-600'] },
      heading: { fontSize: 10, bold: true, margin: [0, 0, 0, 4] },
      label: { color: TOKENS['text-secondary'] },
      muted: { color: TOKENS['text-secondary'] },
      th: { bold: true, color: TOKENS['text-secondary'] },
      banner: { fontSize: 11, bold: true },
    },
    content,
    footer: (page, pages) => ({
      margin: [40, 12, 40, 0],
      columns: [
        {
          text: t(`${prefix}.footer`, {
            at: `${input.generatedAt.slice(0, 16).replace('T', ' ')} UTC`,
          }),
          style: 'muted',
          fontSize: 7,
        },
        {
          text: t(`${prefix}.page`, { page, pages }),
          alignment: 'right',
          style: 'muted',
          fontSize: 7,
        },
      ],
    }),
  };
}
